import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    Divider,
    LinearProgress,
} from '@mui/material';
import { Search, Warning } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useGetJobTitlesQuery, useAddSkillsMutation } from '../../../../../api/preHQ';
import { hasPermission } from '../../../../../utils/permission';

const PositionSkills = ({ form }) => {
    const {
        formState: { errors },
    } = form;

    const [mandatorySkills, setMandatorySkills] = useState(
        form.getValues().mandatorySkills
            ? form.getValues().mandatorySkills.map((skill) => (typeof skill === 'string' ? skill : skill.name || ''))
            : [],
    );

    const [optionalSkills, setOptionalSkills] = useState(
        form.getValues().optionalSkills
            ? form.getValues().optionalSkills.map((skill) => (typeof skill === 'string' ? skill : skill.name || ''))
            : [],
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [activeSearch, setActiveSearch] = useState(null);
    const [jobType, setJobType] = useState('standard-generic');
    const currentUser = useSelector((state) => state.session.user);

    // Use real API hook for skills
    const { data: skillsData, isLoading } = useGetJobTitlesQuery(
        { type: 'skill', query: searchTerm },
        { skip: !searchTerm || searchTerm.length < 2 },
    );

    // Use RTK Query add skills mutation
    const [addSkillsApi, { data: addSkillsApiData }] = useAddSkillsMutation();

    // Transform API data to skills format
    const availableSkills = React.useMemo(() => {
        if (skillsData?.results && Array.isArray(skillsData.results)) {
            return skillsData.results;
        }
        return [];
    }, [skillsData]);

    const filteredSkills = availableSkills || [];

    // Update job type based on API response
    useEffect(() => {
        setJobType(addSkillsApiData?.matchingRoles?.length !== 0 ? 'standard-generic' : 'standard-niche');
    }, [addSkillsApiData?.matchingRoles]);

    // Update form values whenever skills change
    useEffect(() => {
        form.setValue('mandatorySkills', mandatorySkills, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [mandatorySkills, form]);

    useEffect(() => {
        form.setValue('optionalSkills', optionalSkills, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [optionalSkills, form]);

    const removeSkill = (skillToRemove, type) => {
        if (type === 'mandatory') {
            setMandatorySkills((skills) => {
                const updatedSkills = skills.filter((skill) => skill !== skillToRemove);
                const skillsPayload = {
                    skills: updatedSkills,
                };
                addSkillsApi(skillsPayload);
                return updatedSkills;
            });
        } else {
            setOptionalSkills((skills) => skills.filter((skill) => skill !== skillToRemove));
        }
    };

    const addSkill = (skill, type) => {
        if (type === 'mandatory') {
            if (mandatorySkills.length >= 7) {
                return;
            }

            setMandatorySkills((prev) => {
                const existingIndex = prev.findIndex((s) => s.toLowerCase() === skill.toLowerCase());
                const filteredSkills =
                    existingIndex >= 0
                        ? [...prev.slice(0, existingIndex), ...prev.slice(existingIndex + 1)]
                        : [...prev];
                const updatedSkills = [...filteredSkills, skill];

                const skillsPayload = {
                    skills: updatedSkills,
                };
                addSkillsApi(skillsPayload);

                return updatedSkills;
            });
        } else {
            setOptionalSkills((prev) => {
                const existingIndex = prev.findIndex((s) => s.toLowerCase() === skill.toLowerCase());
                const filteredSkills =
                    existingIndex >= 0
                        ? [...prev.slice(0, existingIndex), ...prev.slice(existingIndex + 1)]
                        : [...prev];
                return [...filteredSkills, skill];
            });
        }
        setSearchTerm('');
        setActiveSearch(null);
    };

    const hasTalentPoolPermission = useMemo(
        () => hasPermission(currentUser.authorities, ['ROLE_BASE_PERMISSION_PRE_HQ_TALENT_POOL_SECTION_POSITIONS']),
        [currentUser.authorities],
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Grid container spacing={3}>
                {/* Position Skills Section */}
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            mb: 4,
                        }}
                    >
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 500 }}>
                            Position Skills
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
                            {/* Mandatory Skills Column */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 500 }}>
                                    Mandatory Skills (Max 7)
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {mandatorySkills.map((skill, index) => (
                                            <Chip
                                                key={index}
                                                label={skill}
                                                onDelete={() => removeSkill(skill, 'mandatory')}
                                                sx={{
                                                    bgcolor: '#FFF4F0',
                                                    color: '#ED7342',
                                                    '&:hover': {
                                                        bgcolor: '#FFF4F0',
                                                        opacity: 0.8,
                                                    },
                                                    '& .MuiChip-deleteIcon': {
                                                        color: '#ED7342',
                                                        '&:hover': {
                                                            color: '#ED7342',
                                                            opacity: 0.8,
                                                        },
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    {errors.mandatorySkills && (
                                        <Alert severity="error" icon={<Warning />}>
                                            {errors.mandatorySkills.message}
                                        </Alert>
                                    )}
                                    <Box>
                                        <Typography
                                            component="button"
                                            onClick={() => setActiveSearch('mandatory')}
                                            sx={{
                                                fontSize: '0.875rem',
                                                color: '#ED7342',
                                                '&:hover': {
                                                    color: '#ED7342',
                                                    opacity: 0.9,
                                                },
                                                cursor: mandatorySkills.length >= 7 ? 'not-allowed' : 'pointer',
                                                opacity: mandatorySkills.length >= 7 ? 0.5 : 1,
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                            disabled={mandatorySkills.length >= 7}
                                        >
                                            <span>+</span>
                                            Add Skills
                                        </Typography>
                                        {activeSearch === 'mandatory' && (
                                            <Box sx={{ position: 'relative', mt: 1 }}>
                                                <TextField
                                                    placeholder="Search skills..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{
                                                        startAdornment: <Search sx={{ color: 'grey.400', mr: 1 }} />,
                                                    }}
                                                    autoFocus
                                                />
                                                {isLoading ? (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            zIndex: 10,
                                                            width: '100%',
                                                            bgcolor: 'white',
                                                            boxShadow: 3,
                                                            borderRadius: 1,
                                                            mt: 0.5,
                                                            border: 1,
                                                            borderColor: 'divider',
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <CircularProgress size={16} sx={{ color: '#ED7342' }} />
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    searchTerm && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                zIndex: 10,
                                                                width: '100%',
                                                                bgcolor: 'white',
                                                                boxShadow: 3,
                                                                borderRadius: 1,
                                                                mt: 0.5,
                                                                border: 1,
                                                                borderColor: 'divider',
                                                                maxHeight: 192,
                                                                overflow: 'auto',
                                                            }}
                                                        >
                                                            {filteredSkills.length > 0 ? (
                                                                filteredSkills.map((skill, index) => (
                                                                    <Box
                                                                        key={index}
                                                                        component="button"
                                                                        onClick={() => addSkill(skill, 'mandatory')}
                                                                        sx={{
                                                                            width: '100%',
                                                                            textAlign: 'left',
                                                                            px: 2,
                                                                            py: 1,
                                                                            '&:hover': {
                                                                                bgcolor: '#FFF4F0',
                                                                            },
                                                                            fontSize: '0.875rem',
                                                                            border: 'none',
                                                                            bgcolor: 'transparent',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        {skill}
                                                                    </Box>
                                                                ))
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        fontSize: '0.875rem',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    No skills found
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Divider */}
                            <Divider orientation="vertical" flexItem />

                            {/* Optional Skills Column */}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 500 }}>
                                    Optional Skills
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {optionalSkills.map((skill, index) => (
                                            <Chip
                                                key={index}
                                                label={skill}
                                                onDelete={() => removeSkill(skill, 'optional')}
                                                variant="outlined"
                                                sx={{
                                                    borderColor: '#ED7342',
                                                    color: '#ED7342',
                                                    '&:hover': {
                                                        bgcolor: '#FFF4F0',
                                                    },
                                                    '& .MuiChip-deleteIcon': {
                                                        color: '#ED7342',
                                                        '&:hover': {
                                                            color: '#ED7342',
                                                            opacity: 0.8,
                                                        },
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Box>
                                        <Typography
                                            component="button"
                                            onClick={() => setActiveSearch('optional')}
                                            sx={{
                                                fontSize: '0.875rem',
                                                color: '#ED7342',
                                                '&:hover': {
                                                    color: '#ED7342',
                                                    opacity: 0.9,
                                                },
                                                cursor: 'pointer',
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <span>+</span>
                                            Add Skills
                                        </Typography>
                                        {activeSearch === 'optional' && (
                                            <Box sx={{ position: 'relative', mt: 1 }}>
                                                <TextField
                                                    placeholder="Search skills..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{
                                                        startAdornment: <Search sx={{ color: 'grey.400', mr: 1 }} />,
                                                    }}
                                                    autoFocus
                                                />
                                                {isLoading ? (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            zIndex: 10,
                                                            width: '100%',
                                                            bgcolor: 'white',
                                                            boxShadow: 3,
                                                            borderRadius: 1,
                                                            mt: 0.5,
                                                            border: 1,
                                                            borderColor: 'divider',
                                                            p: 2,
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <CircularProgress size={16} sx={{ color: '#ED7342' }} />
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    searchTerm && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                zIndex: 10,
                                                                width: '100%',
                                                                bgcolor: 'white',
                                                                boxShadow: 3,
                                                                borderRadius: 1,
                                                                mt: 0.5,
                                                                border: 1,
                                                                borderColor: 'divider',
                                                                maxHeight: 192,
                                                                overflow: 'auto',
                                                            }}
                                                        >
                                                            {filteredSkills.length > 0 ? (
                                                                filteredSkills.map((skill, index) => (
                                                                    <Box
                                                                        key={index}
                                                                        component="button"
                                                                        onClick={() => addSkill(skill, 'optional')}
                                                                        sx={{
                                                                            width: '100%',
                                                                            textAlign: 'left',
                                                                            px: 2,
                                                                            py: 1,
                                                                            '&:hover': {
                                                                                bgcolor: '#FFF4F0',
                                                                            },
                                                                            fontSize: '0.875rem',
                                                                            border: 'none',
                                                                            bgcolor: 'transparent',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        {skill}
                                                                    </Box>
                                                                ))
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        fontSize: '0.875rem',
                                                                        color: 'text.secondary',
                                                                    }}
                                                                >
                                                                    No skills found
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Insights Section */}
            </Grid>
        </Box>
    );
};

export default PositionSkills;
